import { inject, ref, computed, getCurrentInstance, onMounted, isRef, useAttrs } from 'vue';
import { unrefElement, useTimeoutFn, whenever } from '@vueuse/core';

export const FormKey = Symbol('form');

// Fallthrough attrs that apply to the form/field container,
// not to each field control.
const excludeAttrs = new Set(['model', 'class', 'style']);

export const useFormContext = () => {
    return inject(FormKey, null);
};

export const useValidation = (props = {}) => {
    const form = useFormContext();
    const vm = getCurrentInstance();
    const name = isRef(props.name) ? props.name : computed(() => props.name);
    const target = isRef(props.target) ? props.target : ref(null);

    const invalid = computed(() => {
        return form?.errors.has(name.value) ?? false;
    });

    const error = computed(() => {
        return invalid.value ? form.errors.get(name.value) : null;
    });

    const ownAttrs = useAttrs();

    const attrs = computed(() => {
        return [form?.$attrs ?? {}, ownAttrs]
            .flatMap((source) => Object.entries(source))
            .filter(([key]) => !excludeAttrs.has(key))
            .reduce((attrs, [key, value]) => {
                return Object.assign(attrs, { [key]: value });
            }, {});
    });

    const clearErrors = () => {
        form?.errors.clear(name.value);
    }

    const setError = (message) => {
        form?.errors.set(name.value, message);
    }

    const focus = () => {
        unrefElement(target.value)?.focus();
    }

    onMounted(() => {
        form?.formFields.push(vm);
    });

    return {
        invalid,
        error,
        attrs,
        focus,
        target,
        clearErrors,
        setError,
    };
}

export const useToggleOnTime = (time = 5000) => {
   const model = ref(false);

   const { start } = useTimeoutFn(() => {
    model.value = false;
   }, time, { immediate: false });

   whenever(model, () => {
    if (model.value) {
        start();
    }
   })

   return model;
}